import { type UseFormReturn, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as z from 'zod';

import { useGetParking } from 'src/features/parkingManagement/api/useGetParking.ts';
import { NOTIFICATIONS } from 'src/features/parkingManagement/types/notifications.ts';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { notificationTitleMapping } from 'src/features/parkingManagement/utils/notificationsTableColumns';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { membershipRoleOptions } from 'src/features/userManagement/utils';
import { useParking } from 'src/providers/ParkingProvider';
import {
    Form,
    FormControl,
    FormControlStack,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form.tsx';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select.tsx';
import { generalMessages } from 'src/utils/messages.ts';

export type CreateNotificationSettingsFormSchemaValues = z.infer<typeof createNotificationSettingsFormSchema>;
export const createNotificationSettingsFormSchema = z
    .object({
        notification_type: z.nativeEnum(NOTIFICATIONS),
        is_enabled: z.boolean(),
        recipient_roles: z.array(
            z.enum([
                MEMBERSHIP_ROLES.VALIDATOR,
                MEMBERSHIP_ROLES.OWNER,
                MEMBERSHIP_ROLES.FINANCE,
                MEMBERSHIP_ROLES.VIEWER,
                MEMBERSHIP_ROLES.OPERATOR,
                MEMBERSHIP_ROLES.PARKING_MANAGER,
                MEMBERSHIP_ROLES.RECEPTION,
                MEMBERSHIP_ROLES.SALES_MANAGER,
                MEMBERSHIP_ROLES.RECEPTION_MANAGER,
            ]),
        ),
    })
    .superRefine((data, ctx) => {
        if (data.is_enabled && data.recipient_roles.length === 0) {
            ctx.addIssue({
                path: ['recipient_roles'],
                code: z.ZodIssueCode.custom,
            });
        }
    });

interface NotificationFormProps {
    form: UseFormReturn<CreateNotificationSettingsFormSchemaValues>;
    onSubmit: () => void;
}

const NotificationForm = ({ form, onSubmit }: NotificationFormProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const allowedRoles: MEMBERSHIP_ROLES[] = useGetParking({ parkingId }).data?.roles || [];

    const enabledNotificationMapping = {
        [formatMessage(generalMessages.yes)]: 'true',
        [formatMessage(generalMessages.no)]: 'false',
    };

    const isEnabled = useWatch({
        control: form.control,
        name: 'is_enabled',
    });

    const allowedRolesList = Object.values(MEMBERSHIP_ROLES).filter(
        (role) => role !== MEMBERSHIP_ROLES.CLIENT && allowedRoles.includes(role),
    );

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
                control={form.control}
                name="notification_type"
                render={({ field }) => (
                    <FormItem variant="dialog" className="w-full">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...parkingManagementMessages.notification} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="flex flex-col">
                                    <div className="text-sm font-bold">
                                        {formatMessage(notificationTitleMapping[field.value].title)}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        {formatMessage(notificationTitleMapping[field.value].subTitle)}
                                    </div>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="is_enabled"
                render={() => (
                    <FormItem variant="dialog" className="w-full">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...parkingManagementMessages.enabled} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={(val) =>
                                        form.setValue('is_enabled', val === 'true', {
                                            shouldDirty: true,
                                        })
                                    }
                                    value={String(form.watch('is_enabled'))}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={formatMessage(parkingManagementMessages.enabled)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(enabledNotificationMapping).map(([key, value]) => {
                                            return (
                                                <SelectItem key={key} value={value}>
                                                    {key}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="recipient_roles"
                render={({ field }) => {
                    const { ref: _, ...rest } = field;
                    const fieldData = field.value
                        ?.map((role) => {
                            const label = membershipRoleOptions.find((option) => option.value === role)?.label;
                            return label ? formatMessage(label) : null;
                        })
                        .filter((label): label is string => typeof label === 'string')
                        .join(', ');

                    const allValues = allowedRolesList
                        .map((role: MEMBERSHIP_ROLES) => {
                            return membershipRoleOptions.find((option) => option.value === role)?.value;
                        })
                        .filter((item) => item !== undefined);

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...parkingManagementMessages.recipientRoles} />
                                <span className={isEnabled ? 'text-blue-800' : 'invisible'}>*</span>
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect {...rest}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl className="w-full" disabled={!isEnabled}>
                                                {fieldData.length > 0 && isEnabled ? (
                                                    fieldData
                                                ) : (
                                                    <span className="text-gray-400">
                                                        <FormattedMessage
                                                            {...parkingManagementMessages.recipientRoles}
                                                        />
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {allowedRolesList.map((role: MEMBERSHIP_ROLES) => {
                                                const label = membershipRoleOptions.find(
                                                    (recipient) => recipient.value === role,
                                                )?.label;
                                                const translatedLabel = label ? formatMessage(label) : '-';
                                                return (
                                                    <MultiSelectOption key={role} selected={role}>
                                                        {translatedLabel}
                                                    </MultiSelectOption>
                                                );
                                            })}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
        </Form>
    );
};

export default NotificationForm;
